import axios from "axios";
import {
  UNAUTHENTICATED_ERROR_EVENT,
  UNAUTHORIZED_ERROR_EVENT,
} from "./constants";
import { v4 as uuidv4 } from "uuid";
import { APIendpoints } from "../utils/constants/APIendpoints";
import { APP_TYPES, getAppType } from "utils/appConfig";
import {
  getCookie,
  deleteCookie,
  AUTH_TOKEN_COOKIE,
  OUTLET_ID_COOKIE,
} from "utils/cookies";
import { LS_KEYS } from "utils/constants/localStorageKeys";

const EXTERNAL_AUTH_ENDPOINTS = new Set([
  "/api/verifyUser",
  "/api/user/otpsms",
  "/api/registration/signin",
]);

const EXTERNAL_PUBLIC_ENDPOINTS = new Set([
  ...EXTERNAL_AUTH_ENDPOINTS,
  APIendpoints.sendSellerAppTracking,
]);

const getRequestPath = (config) => String(config?.url || "").split("?")[0];

const clearSessionAndRedirect = () => {
  window.localStorage.removeItem(LS_KEYS.TOKEN);
  window.localStorage.removeItem(LS_KEYS.OUTLET_ID);
  window.localStorage.removeItem(LS_KEYS.OUTLET_NAME);
  // Also remove legacy camelCase keys that may still exist in older sessions.
  window.localStorage.removeItem(LS_KEYS.OUTLET_ID_CAMEL);
  window.localStorage.removeItem(LS_KEYS.OUTLET_NAME_CAMEL);
  deleteCookie(AUTH_TOKEN_COOKIE);
  deleteCookie(OUTLET_ID_COOKIE);
  if (window && window.logoutChannelMessageHandler) {
    window.logoutChannelMessageHandler.postMessage("true");
  }
  // replace() navigates away cleanly; reload() re-runs API calls before auth is set up → 401 loop.
  if (getAppType() === APP_TYPES.EXTERNAL) {
    window.location.replace("/rk");
  } else {
    const redirect = encodeURIComponent(window.location.pathname);
    window.location.replace(`/login?redirect=${redirect}`);
  }
};

export const axiosInterceptor = () => {
  axios.interceptors.request.use(
    function (config) {
      config.headers["X-Trackingid"] = `${uuidv4()}`;
      const isExternal = getAppType() === APP_TYPES.EXTERNAL;
      if (!isExternal) {
        config.headers["x-client"] = "seller";
      }
      const token = isExternal
        ? getCookie(AUTH_TOKEN_COOKIE)
        : window.localStorage.getItem("token");

      const requestPath = getRequestPath(config);
      const isPublicExternalEndpoint =
        EXTERNAL_PUBLIC_ENDPOINTS.has(requestPath);
      if (isExternal && !isPublicExternalEndpoint && !token) {
        if (window.location.pathname !== "/rk") {
          window.location.replace("/rk");
        }
        return Promise.reject(
          new axios.CanceledError("Authentication is required")
        );
      }

      if (token) {
        config.headers["Authorization"] = token;
      }
      if (isExternal) {
        const outletId = getCookie(OUTLET_ID_COOKIE);
        if (outletId) {
          config.headers["X-OutletId"] = outletId;
        }
      }
      const routingContext = window.localStorage.getItem("routingContext");
      if (routingContext !== "") {
        config.headers["routing_context"] = routingContext;
      }

      const appType = window.localStorage.getItem("apptype");
      if (appType) {
        config.headers["apptype"] = appType;
      }

      return config;
    },
    function (error) {
      const isS3Url =
        error.config &&
        /(s3-|s3\.)?(.*)\.amazonaws\.com/.test(error.config.url);
      if (!isS3Url && [401, 410].includes(error?.response?.status)) {
        alert("Session Expired! Please login again");

        window.localStorage.removeItem(LS_KEYS.TOKEN);
        window.localStorage.removeItem(LS_KEYS.OUTLET_ID);
        window.localStorage.removeItem(LS_KEYS.OUTLET_NAME);
        // Also remove legacy camelCase keys that may still exist in older sessions.
        window.localStorage.removeItem(LS_KEYS.OUTLET_ID_CAMEL);
        window.localStorage.removeItem(LS_KEYS.OUTLET_NAME_CAMEL);
        deleteCookie(AUTH_TOKEN_COOKIE);
        deleteCookie(OUTLET_ID_COOKIE);
        if (window && window.logoutChannelMessageHandler) {
          window.logoutChannelMessageHandler.postMessage("true");
        }
      }

      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      const requestPath = getRequestPath(error.config);
      const isExternal = getAppType() === APP_TYPES.EXTERNAL;
      const isTrackingEndpoint =
        requestPath === APIendpoints.sendSellerAppTracking;
      const isExternalAuthEndpoint =
        isExternal && EXTERNAL_AUTH_ENDPOINTS.has(requestPath);

      if ([401, 410].includes(error?.response?.status)) {
        if (isTrackingEndpoint || isExternalAuthEndpoint) {
          if (isExternalAuthEndpoint && window.location.pathname !== "/rk") {
            window.location.replace("/rk");
          }
          return Promise.reject(error);
        }
        document.dispatchEvent(new CustomEvent(UNAUTHENTICATED_ERROR_EVENT));
        clearSessionAndRedirect();
      } else if (error?.response?.status === 403) {
        const isPurchaseOrderListEndpoint =
          requestPath === APIendpoints.getPurchaseOrderList;
        if (isPurchaseOrderListEndpoint) {
          clearSessionAndRedirect();
        } else {
          document.dispatchEvent(new CustomEvent(UNAUTHORIZED_ERROR_EVENT));
        }
      }
      return Promise.reject(error);
    }
  );
};
